/**
 * @fileoverview ApproverScopeControl — whose time one approver approves
 * (MOR-1017). Sits on an approver's row in Timesheet Settings.
 *
 * Two explicit states rather than one multi-select where "nothing chosen"
 * would have to mean something: "Everyone" (firm-wide, the V1 behaviour) or
 * "Selected teams". Selected teams with none chosen is a real state — it
 * approves nobody — and is also where an approver lands when their only team
 * is deleted.
 */

import type { Id } from "@cvx/_generated/dataModel";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { MultiSearchableSelect } from "@/ui/composed/inputs/multi-searchable-select";

const EVERYONE = "everyone";
const TEAMS = "teams";

export function ApproverScopeControl({
  memberName,
  approverTeamIds,
  teams,
  disabled,
  onChange,
}: {
  memberName: string;
  /** `undefined` = everyone. A list (even empty) = limited to those teams. */
  approverTeamIds: Id<"teams">[] | undefined;
  teams: { _id: Id<"teams">; name: string }[];
  disabled: boolean;
  /** `null` = everyone. */
  onChange: (teamIds: Id<"teams">[] | null) => void;
}) {
  const limited = approverTeamIds !== undefined;
  // A team deleted elsewhere may linger in the list until it is next saved.
  const chosen = (approverTeamIds ?? []).filter((id) =>
    teams.some((t) => t._id === id),
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-muted-foreground">Approves</span>
      <Select
        value={limited ? TEAMS : EVERYONE}
        onValueChange={(value) => onChange(value === TEAMS ? chosen : null)}
        disabled={disabled}
      >
        <SelectTrigger
          className="h-8 w-40"
          aria-label={`Who ${memberName} approves`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={EVERYONE}>Everyone</SelectItem>
          <SelectItem value={TEAMS}>Selected teams</SelectItem>
        </SelectContent>
      </Select>
      {limited && disabled && (
        // The picker has no read-only mode, and a live picker on a read-only
        // screen let a member submit a change the server then refused with an
        // unrelated message (QA-44). Plain text instead.
        <span className="text-sm">
          {chosen.length > 0
            ? teams
                .filter((t) => chosen.includes(t._id))
                .map((t) => t.name)
                .join(", ")
            : "No team chosen"}
        </span>
      )}
      {limited && !disabled && (
        <MultiSearchableSelect
          value={chosen}
          onValueChange={(values) => onChange(values as Id<"teams">[])}
          options={teams.map((t) => ({ value: t._id, label: t.name }))}
          placeholder="Choose teams…"
          searchPlaceholder="Search teams…"
          emptyMessage={
            teams.length === 0
              ? "No teams yet. Create them in Organisation settings."
              : "No teams found."
          }
          className="h-8 min-w-48"
        />
      )}
    </div>
  );
}
